import { useState, useEffect } from 'react';
import { useAISettings } from '../../hooks/useAISettings';
import { AIProvider, AIConfig, AIRateLimits } from '../../types';
import { AI_PROVIDERS, AI_PROVIDER_LIST } from '../../utils/constants';
import {
  runBackfillEmbeddingsUntilDone,
  type BackfillEmbeddingsResponse,
} from '../../services/invokeBackfillEmbeddings';

const AISettingsPanel = () => {
  const {
    aiSettings,
    aiSettingsLoading,
    aiSettingsError,
    getProviderSettings,
    getDefaultSettings,
    updateSettingsAsync,
    updateSettingsLoading,
  } = useAISettings();

  const [activeProvider, setActiveProvider] = useState<AIProvider>('openai');
  const [showApiKey, setShowApiKey] = useState<Record<AIProvider, boolean>>({
    openai: false,
    anthropic: false,
    google: false,
    perplexity: false,
    other: false,
  });
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [backfillResult, setBackfillResult] = useState<BackfillEmbeddingsResponse | null>(null);
  const [backfillProgress, setBackfillProgress] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // Form state for each provider
  const [formData, setFormData] = useState<Record<AIProvider, {
    enabled: boolean;
    apiKey: string;
    defaultModel: string;
    config: AIConfig;
    rateLimits: AIRateLimits;
  }>>(() => {
    const initial: Record<AIProvider, any> = {} as any;
    AI_PROVIDER_LIST.forEach(provider => {
      const existing = getProviderSettings(provider.id);
      const defaults = getDefaultSettings(provider.id);
      initial[provider.id] = {
        enabled: existing?.enabled ?? false,
        apiKey: '', // Never show actual API key, only allow updating
        defaultModel: existing?.default_model ?? defaults.default_model ?? provider.defaultModel,
        config: existing?.config ?? defaults.config ?? provider.defaultConfig,
        rateLimits: existing?.rate_limits ?? defaults.rate_limits ?? provider.defaultRateLimits,
      };
    });
    return initial;
  });

  // Update form data when settings load
  useEffect(() => {
    if (aiSettings.length > 0 || !aiSettingsLoading) {
      AI_PROVIDER_LIST.forEach(provider => {
        const existing = getProviderSettings(provider.id);
        const defaults = getDefaultSettings(provider.id);
        setFormData(prev => ({
          ...prev,
          [provider.id]: {
            enabled: existing?.enabled ?? false,
            apiKey: '', // Reset API key field
            defaultModel: existing?.default_model ?? defaults.default_model ?? provider.defaultModel,
            config: existing?.config ?? defaults.config ?? provider.defaultConfig,
            rateLimits: existing?.rate_limits ?? defaults.rate_limits ?? provider.defaultRateLimits,
          },
        }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSettings, aiSettingsLoading]);

  const handleSave = async (provider: AIProvider) => {
    const data = formData[provider];
    const providerInfo = AI_PROVIDERS[provider];
    const rawKey = data.apiKey?.trim() ?? '';
    // Never submit masked bullet placeholders as a real key
    const isBulletOnly = /^[•·.]+$/.test(rawKey);
    const apiKeyToSave = rawKey && !isBulletOnly ? rawKey : undefined;

    setSaveError(null);
    setSaveSuccess(null);

    try {
      await updateSettingsAsync({
        provider,
        updates: {
          enabled: data.enabled,
          api_key_encrypted: apiKeyToSave,
          default_model: data.defaultModel || providerInfo.defaultModel,
          config: data.config,
          rate_limits: data.rateLimits,
        },
      });
      updateFormField(provider, 'apiKey', '');
      setSaveSuccess(
        apiKeyToSave
          ? `${providerInfo.name} settings and API key saved.`
          : `${providerInfo.name} settings saved.`
      );
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save AI settings.');
    }
  };

  const updateFormField = <K extends keyof typeof formData[AIProvider]>(
    provider: AIProvider,
    field: K,
    value: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value,
      },
    }));
  };

  const updateConfigField = (provider: AIProvider, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        config: {
          ...prev[provider].config,
          [field]: value,
        },
      },
    }));
  };

  const updateRateLimitField = (provider: AIProvider, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        rateLimits: {
          ...prev[provider].rateLimits,
          [field]: value,
        },
      },
    }));
  };

  const getMaskedApiKey = (provider: AIProvider): string => {
    const existing = getProviderSettings(provider);
    if (existing?.api_key_encrypted) {
      const key = existing.api_key_encrypted;
      if (key.length > 8) {
        return '•'.repeat(Math.min(key.length - 4, 24)) + key.slice(-4);
      }
      return '••••••••';
    }
    return '';
  };

  const handleBackfillEmbeddings = async () => {
    setBackfillLoading(true);
    setBackfillError(null);
    setBackfillProgress(null);
    try {
      const summary = await runBackfillEmbeddingsUntilDone({
        onProgress: ({ round, embedded, failed }) => {
          setBackfillProgress(`Batch ${round}… embedded ${embedded}, failed ${failed}`);
        },
      });
      setBackfillResult({
        message: summary.message,
        embedded: summary.embedded,
        failed: summary.failed,
        lastError: summary.lastError,
      });
      setBackfillProgress(null);
    } catch (err) {
      setBackfillResult(null);
      setBackfillProgress(null);
      setBackfillError(err instanceof Error ? err.message : 'Backfill failed.');
    } finally {
      setBackfillLoading(false);
    }
  };

  const currentProvider = AI_PROVIDERS[activeProvider];
  const currentData = formData[activeProvider];
  const existingSettings = getProviderSettings(activeProvider);
  const hasApiKey = !!existingSettings?.api_key_encrypted;
  const openAiConfigured = !!getProviderSettings('openai')?.api_key_encrypted;

  return (
    <div className="space-y-6">
      {/* Risk Analyst / RAG embeddings */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900">Risk analyst embeddings</h3>
        <p className="text-sm text-gray-500 mt-1">
          Embed suppliers, news, and mitigation tasks missing vectors so Risk analyst can answer from
          platform data. One click runs until complete (batches of 100 rows per table under the hood).
          Also starts automatically after Excel / Master Database import. Requires OpenAI enabled below.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleBackfillEmbeddings}
            disabled={backfillLoading}
            className="px-5 py-2.5 bg-black text-white rounded-full text-sm font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfillLoading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
                {backfillProgress ?? 'Backfilling…'}
              </>
            ) : (
              'Backfill all embeddings'
            )}
          </button>
          {!openAiConfigured && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
              Configure OpenAI below first
            </span>
          )}
        </div>
        {backfillError && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {backfillError}
          </p>
        )}
        {backfillResult && !backfillError && (
          <div className="mt-3 text-sm text-gray-700 space-y-1">
            <p>{backfillResult.message}</p>
            <p>
              Embedded: <strong>{backfillResult.embedded}</strong>
              {' · '}
              Failed: <strong>{backfillResult.failed}</strong>
            </p>
            {backfillResult.lastError && (
              <p className="text-amber-800">Last error: {backfillResult.lastError}</p>
            )}
          </div>
        )}
      </div>

      {/* Provider Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {AI_PROVIDER_LIST.map((provider) => {
            const settings = getProviderSettings(provider.id);
            const isEnabled = settings?.enabled ?? false;
            return (
              <button
                key={provider.id}
                onClick={() => {
                  setActiveProvider(provider.id);
                  setSaveError(null);
                  setSaveSuccess(null);
                }}
                className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm whitespace-nowrap border-b-2 transition-colors ${
                  activeProvider === provider.id
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-black hover:border-gray-300'
                }`}
              >
                <span>{provider.name}</span>
                {isEnabled && (
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Error Display */}
      {aiSettingsError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            {aiSettingsError instanceof Error ? aiSettingsError.message : 'Failed to load AI settings'}
          </p>
        </div>
      )}

      {saveError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-sm text-red-600">{saveError}</p>
        </div>
      )}

      {saveSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg" role="status">
          <p className="text-sm text-green-800">{saveSuccess}</p>
        </div>
      )}

      {/* Loading State */}
      {aiSettingsLoading && (
        <div className="p-8 text-center text-gray-400">
          <i className="fa-solid fa-spinner fa-spin mr-2"></i>Loading AI settings...
        </div>
      )}

      {/* Provider Configuration Form */}
      {!aiSettingsLoading && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-xl font-bold">{currentProvider.name}</h3>
            <p className="text-sm text-gray-500 mt-1">{currentProvider.description}</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="font-semibold text-gray-700">Enable Provider</label>
                <p className="text-sm text-gray-500">Enable this AI provider for use</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentData.enabled}
                  onChange={(e) => updateFormField(activeProvider, 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-black/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
              </label>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                API Key {hasApiKey && <span className="text-green-600 text-xs">(configured)</span>}
              </label>
              {hasApiKey && !currentData.apiKey && (
                <p className="text-xs text-gray-500 mb-2 font-mono tracking-wider" aria-hidden>
                  {getMaskedApiKey(activeProvider)}
                </p>
              )}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type={showApiKey[activeProvider] ? 'text' : 'password'}
                    value={currentData.apiKey}
                    onChange={(e) => {
                      setSaveError(null);
                      setSaveSuccess(null);
                      updateFormField(activeProvider, 'apiKey', e.target.value);
                    }}
                    placeholder={hasApiKey ? 'Enter new API key to update' : 'Enter API key'}
                    autoComplete="off"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(prev => ({ ...prev, [activeProvider]: !prev[activeProvider] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                    aria-label={showApiKey[activeProvider] ? 'Hide API key' : 'Show API key'}
                  >
                    <i className={`fa-solid ${showApiKey[activeProvider] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {hasApiKey ? 'Leave empty to keep existing key. Enter new key to update.' : 'API key will be encrypted and stored securely via manage-ai-keys.'}
              </p>
            </div>

            {/* Default Model */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Default Model</label>
              <select
                value={currentData.defaultModel}
                onChange={(e) => updateFormField(activeProvider, 'defaultModel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
              >
                {currentProvider.models.length > 0 ? (
                  currentProvider.models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} {model.description && `- ${model.description}`}
                    </option>
                  ))
                ) : (
                  <option value="">Enter model name</option>
                )}
              </select>
            </div>

            {/* Configuration Parameters */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-bold text-gray-700 mb-4">Model Parameters</h4>
              <div className="space-y-4">
                {/* Temperature */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Temperature</label>
                    <span className="text-sm text-gray-500">{currentData.config.temperature ?? 1.0}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={currentData.config.temperature ?? 1.0}
                    onChange={(e) => updateConfigField(activeProvider, 'temperature', parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Controls randomness (0 = deterministic, 2 = creative)</p>
                </div>

                {/* Max Tokens */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
                  <input
                    type="number"
                    min="1"
                    max={currentProvider.models.find(m => m.id === currentData.defaultModel)?.max_tokens ?? 8192}
                    value={currentData.config.max_tokens ?? 2048}
                    onChange={(e) => updateConfigField(activeProvider, 'max_tokens', parseInt(e.target.value) || 2048)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                  />
                </div>

                {/* Top P */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Top P</label>
                    <span className="text-sm text-gray-500">{currentData.config.top_p ?? 1.0}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={currentData.config.top_p ?? 1.0}
                    onChange={(e) => updateConfigField(activeProvider, 'top_p', parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Nucleus sampling threshold</p>
                </div>

                {/* Top K (for Google) */}
                {activeProvider === 'google' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Top K</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={currentData.config.top_k ?? 40}
                      onChange={(e) => updateConfigField(activeProvider, 'top_k', parseInt(e.target.value) || 40)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                    />
                  </div>
                )}

                {/* Frequency Penalty (for OpenAI) */}
                {(activeProvider === 'openai' || activeProvider === 'other') && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">Frequency Penalty</label>
                      <span className="text-sm text-gray-500">{currentData.config.frequency_penalty ?? 0.0}</span>
                    </div>
                    <input
                      type="range"
                      min="-2"
                      max="2"
                      step="0.1"
                      value={currentData.config.frequency_penalty ?? 0.0}
                      onChange={(e) => updateConfigField(activeProvider, 'frequency_penalty', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                )}

                {/* Presence Penalty (for OpenAI) */}
                {(activeProvider === 'openai' || activeProvider === 'other') && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">Presence Penalty</label>
                      <span className="text-sm text-gray-500">{currentData.config.presence_penalty ?? 0.0}</span>
                    </div>
                    <input
                      type="range"
                      min="-2"
                      max="2"
                      step="0.1"
                      value={currentData.config.presence_penalty ?? 0.0}
                      onChange={(e) => updateConfigField(activeProvider, 'presence_penalty', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Rate Limits */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-bold text-gray-700 mb-4">Rate Limits</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Requests per Minute</label>
                  <input
                    type="number"
                    min="1"
                    value={currentData.rateLimits.requests_per_minute ?? 60}
                    onChange={(e) => updateRateLimitField(activeProvider, 'requests_per_minute', parseInt(e.target.value) || 60)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tokens per Day</label>
                  <input
                    type="number"
                    min="1"
                    value={currentData.rateLimits.tokens_per_day ?? 1000000}
                    onChange={(e) => updateRateLimitField(activeProvider, 'tokens_per_day', parseInt(e.target.value) || 1000000)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Requests per Day</label>
                  <input
                    type="number"
                    min="1"
                    value={currentData.rateLimits.requests_per_day ?? 10000}
                    onChange={(e) => updateRateLimitField(activeProvider, 'requests_per_day', parseInt(e.target.value) || 10000)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="border-t border-gray-200 pt-6">
              <button
                onClick={() => handleSave(activeProvider)}
                disabled={updateSettingsLoading}
                className="w-full py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateSettingsLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISettingsPanel;
